// WorkBriefGuidePreview.js — compact relevant Codebase Guide rules for Work Briefs.
import React, { useState } from "react";
import {
  guideCategoryLabel,
  guidePriorityLabel,
} from "./codebaseGuideConstants";
import "./WorkBriefGuidePreview.css";

const DEFAULT_COLLAPSE_AFTER = 3;
const BODY_PREVIEW_CHARS = 180;

function previewBody(body) {
  const text = String(body || "").trim();
  if (text.length <= BODY_PREVIEW_CHARS) return text;
  return `${text.slice(0, BODY_PREVIEW_CHARS).trim()}…`;
}

/**
 * @param {object} props
 * @param {Array} props.entries - already-filtered relevant entries (with matchReasons)
 * @param {boolean} [props.loading]
 * @param {string} [props.error]
 * @param {'create'|'detail'} [props.variant]
 * @param {number} [props.collapseAfter]
 */
function WorkBriefGuidePreview({
  entries = [],
  loading = false,
  error = "",
  variant = "detail",
  collapseAfter = DEFAULT_COLLAPSE_AFTER,
}) {
  const [expanded, setExpanded] = useState(false);
  const list = Array.isArray(entries) ? entries : [];
  const needsCollapse = list.length > collapseAfter;
  const visible = expanded || !needsCollapse ? list : list.slice(0, collapseAfter);

  const subtitle =
    variant === "create"
      ? "These repo rules will help shape the Task and developer handoff."
      : "These rules should be considered when building or reviewing this Task.";

  return (
    <section
      className={`work-brief-guide-preview work-brief-guide-preview--${variant}`}
      data-testid="work-brief-guide-preview"
      data-variant={variant}
    >
      <header className="work-brief-guide-preview-header">
        <h5>Relevant Codebase Guide rules</h5>
        <p>{subtitle}</p>
      </header>

      {loading && (
        <p className="work-brief-guide-preview-muted" data-testid="work-brief-guide-loading">
          Loading guide rules…
        </p>
      )}

      {!loading && error && (
        <p
          className="work-brief-guide-preview-error"
          role="status"
          data-testid="work-brief-guide-error"
        >
          {error}
        </p>
      )}

      {!loading && !error && list.length === 0 && (
        <p
          className="work-brief-guide-preview-empty"
          data-testid="work-brief-guide-empty"
        >
          No matching guide rules yet. Add rules in Codebase Guide to guide future Tasks.
        </p>
      )}

      {!loading && list.length > 0 && (
        <ul className="work-brief-guide-preview-list">
          {visible.map((entry) => (
            <li
              key={entry._id}
              className="work-brief-guide-preview-item"
              data-testid={`work-brief-guide-item-${entry._id}`}
            >
              <div className="work-brief-guide-preview-item-top">
                <span className="work-brief-guide-preview-category">
                  {guideCategoryLabel(entry.category)}
                </span>
                <span
                  className={`work-brief-guide-preview-priority priority-${entry.priority}`}
                >
                  {guidePriorityLabel(entry.priority)}
                </span>
              </div>
              <div className="work-brief-guide-preview-title">{entry.title}</div>
              <p className="work-brief-guide-preview-body">{previewBody(entry.body)}</p>
              {(entry.pathScope?.length > 0 || entry.taskTypeScope?.length > 0) && (
                <div className="work-brief-guide-preview-scopes">
                  {entry.pathScope?.map((path) => (
                    <span key={`p-${path}`} className="work-brief-guide-chip">
                      {path}
                    </span>
                  ))}
                  {entry.taskTypeScope?.map((task) => (
                    <span key={`t-${task}`} className="work-brief-guide-chip task">
                      {task}
                    </span>
                  ))}
                </div>
              )}
              {Array.isArray(entry.matchReasons) && entry.matchReasons.length > 0 && (
                <p className="work-brief-guide-preview-reasons">
                  {entry.matchReasons.join(" · ")}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {!loading && needsCollapse && (
        <button
          type="button"
          className="work-brief-guide-preview-toggle"
          data-testid="work-brief-guide-toggle"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? "Show fewer rules"
            : `Show ${list.length - collapseAfter} more rules`}
        </button>
      )}
    </section>
  );
}

export default WorkBriefGuidePreview;
